import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Chip, IconButton, Alert, CircularProgress, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import agentService from '../services/agentService';

const DEFAULT_PLATFORM_SHARE = 30;

const formatCurrency = (amount) =>
  `$${(Number(amount) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    platformSharePercent: String(DEFAULT_PLATFORM_SHARE),
  });
  const [editingShareId, setEditingShareId] = useState(null);
  const [newShareValue, setNewShareValue] = useState('');
  const [savingShareId, setSavingShareId] = useState(null);
  const [athletesDialog, setAthletesDialog] = useState({
    open: false,
    agent: null,
    loading: false,
    data: null,
  });

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await agentService.getAgents();
      setAgents(data);
    } catch (err) {
      setError('Failed to load agents');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return;
    const share = Number(form.platformSharePercent);
    if (Number.isNaN(share) || share < 0 || share > 100) {
      setError('Thravos share must be between 0 and 100');
      return;
    }
    try {
      setCreating(true);
      setError('');
      await agentService.createAgent({
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        platformSharePercent: share,
      });
      setShowCreateModal(false);
      setForm({
        name: '',
        email: '',
        password: '',
        phone: '',
        platformSharePercent: String(DEFAULT_PLATFORM_SHARE),
      });
      fetchAgents();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create agent');
    } finally { setCreating(false); }
  };

  const handleToggleActive = async (agentId) => {
    try { await agentService.toggleAgentActive(agentId); fetchAgents(); }
    catch (err) { setError('Failed to update agent status'); }
  };

  const handleSavePlatformShare = async (agentId) => {
    const share = Number(newShareValue);
    if (Number.isNaN(share) || share < 0 || share > 100) {
      setError('Thravos share must be between 0 and 100');
      return;
    }
    try {
      setSavingShareId(agentId);
      setError('');
      setSuccess('');
      const result = await agentService.updateAgentPlatformShare(agentId, share);
      setEditingShareId(null);
      fetchAgents();
      const count = result?.athletesUpdated ?? 0;
      setSuccess(
        count > 0
          ? `Thravos share set to ${share}%. Updated ${count} athlete${count === 1 ? '' : 's'} under this agent.`
          : `Thravos share set to ${share}%.`
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update Thravos share');
    } finally {
      setSavingShareId(null);
    }
  };

  const handleViewAthletes = async (agent) => {
    setAthletesDialog({ open: true, agent, loading: true, data: null });
    try {
      const data = await agentService.getAgentDashboard(agent._id);
      setAthletesDialog((prev) => ({ ...prev, loading: false, data }));
    } catch (err) {
      setAthletesDialog((prev) => ({ ...prev, loading: false, data: null }));
      setError(err.response?.data?.error || 'Failed to load agent athletes');
    }
  };

  const closeAthletesDialog = () => {
    setAthletesDialog({ open: false, agent: null, loading: false, data: null });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>;
  }

  const dialogAthletes = athletesDialog.data?.athletes || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setShowCreateModal(true)}>Create Agent</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Athletes</TableCell>
                <TableCell>Thravos %</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agents.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: '#8b949e' }}>No agents found. Create one to get started.</TableCell></TableRow>
              ) : agents.map((agent, idx) => (
                <TableRow key={agent._id}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{agent.name}</TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>{agent.email}</TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>{agent.phone || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Chip
                        label={agent.totalAthletes ?? 0}
                        size="small"
                        onClick={() => handleViewAthletes(agent)}
                        sx={{
                          bgcolor: 'rgba(43,182,169,0.15)',
                          color: '#2bb6a9',
                          fontWeight: 700,
                          cursor: 'pointer',
                          minWidth: 36,
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleViewAthletes(agent)}
                        title="View athletes & earnings"
                        sx={{ color: '#8b949e', p: 0.3 }}
                      >
                        <VisibilityIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {editingShareId === agent._id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={newShareValue}
                          onChange={(e) => setNewShareValue(e.target.value)}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{ width: 72 }}
                        />
                        <IconButton
                          size="small"
                          color="success"
                          disabled={savingShareId === agent._id}
                          onClick={() => handleSavePlatformShare(agent._id)}
                        >
                          {savingShareId === agent._id ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                        <IconButton size="small" onClick={() => setEditingShareId(null)} sx={{ color: '#8b949e' }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span style={{ color: '#f47a6a', fontWeight: 600 }}>
                          {agent.platformSharePercent ?? DEFAULT_PLATFORM_SHARE}%
                        </span>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingShareId(agent._id);
                            setNewShareValue(String(agent.platformSharePercent ?? DEFAULT_PLATFORM_SHARE));
                          }}
                          sx={{ color: '#8b949e', p: 0.3 }}
                        >
                          <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={agent.isActive !== false ? 'Active' : 'Inactive'} size="small"
                      sx={{ bgcolor: agent.isActive !== false ? 'rgba(76,175,80,0.15)' : 'rgba(139,148,158,0.15)', color: agent.isActive !== false ? '#81c784' : '#8b949e', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : '—'}</TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" color={agent.isActive !== false ? 'error' : 'success'} onClick={() => handleToggleActive(agent._id)}
                      sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>
                      {agent.isActive !== false ? 'Deactivate' : 'Activate'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Create Agent
          <IconButton onClick={() => setShowCreateModal(false)} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <form onSubmit={handleCreate}>
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField size="small" label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            <TextField size="small" label="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <TextField size="small" label="Password *" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required inputProps={{ minLength: 6 }} />
            <TextField size="small" label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <TextField
              size="small"
              label="Thravos share (%)"
              type="number"
              value={form.platformSharePercent}
              onChange={(e) => setForm({ ...form, platformSharePercent: e.target.value })}
              inputProps={{ min: 0, max: 100 }}
              helperText="Platform cut for this agent's athletes (default 30). Agent + athlete split the remainder."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setShowCreateModal(false)} sx={{ color: '#8b949e' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={creating}>
              {creating ? <CircularProgress size={18} /> : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={athletesDialog.open} onClose={closeAthletesDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Athletes — {athletesDialog.agent?.name}
          <IconButton onClick={closeAthletesDialog} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {athletesDialog.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#2bb6a9' }} />
            </Box>
          ) : !athletesDialog.data ? (
            <Typography sx={{ py: 4, textAlign: 'center', color: '#8b949e' }}>
              Failed to load athletes for this agent.
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  Athletes:{' '}
                  <Box component="span" sx={{ color: '#e6edf3', fontWeight: 700 }}>
                    {athletesDialog.data.totalAthletes ?? 0}
                  </Box>
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  Agent earnings:{' '}
                  <Box component="span" sx={{ color: '#81c784', fontWeight: 700 }}>
                    {formatCurrency(athletesDialog.data.totalAgentCut)}
                  </Box>
                </Typography>
                <Typography variant="body2" sx={{ color: '#8b949e' }}>
                  Athlete gross:{' '}
                  <Box component="span" sx={{ color: '#e6edf3', fontWeight: 700 }}>
                    {formatCurrency(athletesDialog.data.totalAthleteEarnings)}
                  </Box>
                </Typography>
              </Box>

              {dialogAthletes.length === 0 ? (
                <Typography sx={{ py: 4, textAlign: 'center', color: '#8b949e' }}>
                  This agent has no athletes yet.
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Athlete</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Agent %</TableCell>
                        <TableCell>Athlete gross</TableCell>
                        <TableCell>Agent earned</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dialogAthletes.map((athlete) => (
                        <TableRow key={athlete._id}>
                          <TableCell sx={{ fontWeight: 600 }}>{athlete.name}</TableCell>
                          <TableCell sx={{ fontSize: '0.85rem' }}>{athlete.email}</TableCell>
                          <TableCell>{athlete.agentPct ?? athlete.agentCommissionPercent ?? 0}%</TableCell>
                          <TableCell>{formatCurrency(athlete.totalIncome)}</TableCell>
                          <TableCell sx={{ color: '#81c784', fontWeight: 600 }}>
                            {formatCurrency(athlete.agentEarnings)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={athlete.status === 'active' ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                bgcolor: athlete.status === 'active' ? 'rgba(76,175,80,0.15)' : 'rgba(139,148,158,0.15)',
                                color: athlete.status === 'active' ? '#81c784' : '#8b949e',
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAthletesDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Agents;
